/**
 * 埼玉県の背景ツアー「埼玉めぐり」。10か所、それぞれに3つの豆知識。
 *
 * 書いてはいけないこと。
 * - 草加松原には松尾芭蕉の像と文学碑が建っている。像と碑は描かない。
 *   百代橋と矢立橋も昭和60年の構造物なので描かず、松並木と遊歩道だけにする。
 * - 時の鐘の足元には薬師神社がある。鳥居も社殿も構図に入れない。周囲の店の看板も入れない。
 * - 三十槌の氷柱には天然のものと人工のものがある。描くのは天然のほう。キャンプ場の看板は入れない。
 * - 長瀞のライン下りの舟は事業者の営業物なので描かない。
 * - 忍城の御三階櫓は昭和63年の再建で設計の権利が残りうるので入れていない。
 * - 羊山公園の芝桜は愛知の茶臼山高原と絵が重なるので入れていない。
 * - 古代蓮の里の田んぼアートは図柄が著作物なので入れていない。
 * - 秩父夜祭の山車も意匠の権利があるので入れていない。
 * - 吉見百穴は古墳時代末の横穴墓群であり、昭和19年から20年には地下軍需工場が造られ、
 *   3,000人から3,500人の朝鮮人労働者が携わったと吉見町自身が書いている。solemn を立てる。
 */

import type { StageTour } from '../types';

export const saitamaSceneryTour: StageTour = {
  scenes: [
    {
      id: 'nagatoro',
      name: '長瀞 岩畳',
      area: '長瀞町',
      image: '/assets/scenery/saitama/scenes/01_nagatoro.webp',
      hotspots: [
        {
          id: 'nagatoro-designation',
          x: 0.28,
          y: 0.35,
          title: '大正13年に名勝かつ天然記念物',
          body: '大正13年12月9日、名勝および天然記念物（地質鉱物の部）に指定された。範囲は長瀞町と皆野町にまたがる。',
          source: 'https://www.town.nagatoro.saitama.jp/nagatoro-2/nagatoro/',
          mapQuery: '長瀞 岩畳',
        },
        {
          id: 'nagatoro-rocks',
          x: 0.55,
          y: 0.65,
          title: '三波川の結晶片岩',
          body: '三波川の結晶片岩からなり、緑泥石片岩、紅レン石片岩、絹雲母片岩、スチルプノメレン片岩、赤鉄鉱片岩、滑石片岩などが見られる。',
          source: 'https://www.town.nagatoro.saitama.jp/nagatoro-2/nagatoro/',
          mapQuery: '長瀞町 岩畳',
        },
        {
          id: 'nagatoro-geology',
          x: 0.8,
          y: 0.78,
          title: '「日本地質学発祥の地」と呼ばれる',
          body: 'この一帯は「日本地質学発祥の地」とも呼ばれる。三波川の結晶片岩が荒川の流れに浸食されてできた特有の景観である。',
          source: 'https://www.town.nagatoro.saitama.jp/nagatoro-2/nagatoro/',
          mapQuery: '長瀞',
        },
      ],
    },
    {
      id: 'kawahaba',
      name: '荒川の川幅日本一',
      area: '鴻巣市',
      image: '/assets/scenery/saitama/scenes/02_kawahaba.webp',
      hotspots: [
        {
          id: 'kawahaba-width',
          x: 0.3,
          y: 0.4,
          title: '川幅は2537メートル',
          body: '荒川の川幅は2,537メートル。鴻巣市と吉見町の間を流れるエリアの川幅が日本一広い。',
          source: 'https://saitama-riversupporters.pref.saitama.lg.jp/magazine/feature-kounosu/',
          mapQuery: '川幅日本一 鴻巣市',
        },
        {
          id: 'kawahaba-definition',
          x: 0.58,
          y: 0.65,
          title: '「川幅」とは何か',
          body: 'ここでいう川幅とは、河川敷を含めた両岸の堤防の間の幅のことをいう。',
          source: 'https://saitama-riversupporters.pref.saitama.lg.jp/magazine/feature-kounosu/',
          mapQuery: '御成橋 鴻巣市',
        },
        {
          id: 'kawahaba-marker',
          x: 0.8,
          y: 0.82,
          title: '標は2か所にある',
          body: '「川幅日本一」の標は、御成橋のたもとの鴻巣市滝馬室地内と、吉見町大和田地内の荒川右岸堤防の2か所にある。',
          source: 'https://saitama-riversupporters.pref.saitama.lg.jp/magazine/feature-kounosu/',
          mapQuery: '吉見町 荒川',
        },
      ],
    },
    {
      id: 'sakitama',
      name: '埼玉古墳群 丸墓山古墳',
      area: '行田市',
      image: '/assets/scenery/saitama/scenes/03_sakitama.webp',
      hotspots: [
        {
          id: 'sakitama-special',
          x: 0.28,
          y: 0.35,
          title: '令和2年に特別史跡',
          body: '国の特別史跡に指定されたのは令和2年3月10日。現存する大型古墳は9基で、かつては約40基あった。5世紀後半から7世紀初めごろまでの約150年間に次々と造られた。',
          source:
            'https://www.city.gyoda.lg.jp/soshiki/shougaigakusyubu/bunkazaihogo/gyomu/rekishi_bunkazai/1/2336.html',
          mapQuery: '埼玉古墳群',
        },
        {
          id: 'sakitama-size',
          x: 0.55,
          y: 0.6,
          title: '日本で最大級の円墳',
          body: '丸墓山古墳は墳丘径105メートル、墳丘高17.2メートルで、日本で最大級の円墳。二子山古墳は墳丘長132.2メートル、稲荷山古墳は墳丘の主軸長120メートル。',
          source:
            'https://www.city.gyoda.lg.jp/soshiki/shougaigakusyubu/bunkazaihogo/gyomu/rekishi_bunkazai/1/2336.html',
          mapQuery: '丸墓山古墳',
        },
        {
          id: 'sakitama-mitsunari',
          x: 0.8,
          y: 0.78,
          title: '石田三成の陣の伝え',
          body: '1590年、豊臣秀吉の家臣である石田三成が忍城を水攻めにする際、忍城がよく見える丸墓山古墳の頂上に陣を張ったといわれている。',
          source:
            'https://www.city.gyoda.lg.jp/soshiki/shougaigakusyubu/bunkazaihogo/gyomu/rekishi_bunkazai/1/2336.html',
          mapQuery: '行田市 さきたま古墳公園',
        },
      ],
    },
    {
      id: 'menuma',
      name: '妻沼聖天山 歓喜院聖天堂',
      area: '熊谷市',
      image: '/assets/scenery/saitama/scenes/04_menuma.webp',
      hotspots: [
        {
          id: 'menuma-kokuho',
          x: 0.28,
          y: 0.35,
          title: '県内で初めての国宝',
          body: '平成24年7月9日に正式に国宝となった。建造物としては埼玉県内で初めての国宝指定であり、熊谷市では昭和25年の文化財保護法の制定以降、初めての国宝指定だった。',
          source:
            'https://www.city.kumagaya.lg.jp/kanko/midokoro/menumasyoudenzan/kangiinkokuho.html',
          mapQuery: '妻沼聖天山 歓喜院',
        },
        {
          id: 'menuma-built',
          x: 0.55,
          y: 0.6,
          title: '44年かけて建てられた',
          body: '享保20年（1735年）から安永8年（1779年）にかけて建てられた。棟梁は林兵庫正清および正信ら。',
          source:
            'https://www.city.kumagaya.lg.jp/kanko/midokoro/menumasyoudenzan/kangiinkokuho.html',
          mapQuery: '熊谷市 妻沼',
        },
        {
          id: 'menuma-nikko',
          x: 0.8,
          y: 0.8,
          title: '東照宮を直した職人たち',
          body: '日光東照宮の創建から百年あまり後、東照宮の修復にも参加した職人たちによって建てられた。「江戸時代建築の分水嶺」「江戸後期装飾建築の代表例」と評価されている。',
          source:
            'https://www.city.kumagaya.lg.jp/kanko/midokoro/menumasyoudenzan/kangiinkokuho.html',
          mapQuery: '歓喜院聖天堂',
        },
      ],
    },
    {
      id: 'kinchakuda',
      name: '巾着田の曼珠沙華',
      area: '日高市',
      image: '/assets/scenery/saitama/scenes/05_kinchakuda.webp',
      hotspots: [
        {
          id: 'kinchakuda-count',
          x: 0.28,
          y: 0.6,
          title: '500万本、22ヘクタール',
          body: '曼珠沙華は500万本、面積は約22ヘクタール。見頃は秋のお彼岸のころ。',
          source:
            'https://www.city.hidaka.lg.jp/soshiki/shiminseikatsu/sangyoshinko/shokokanko/kanko/kankou/5810.html',
          mapQuery: '巾着田曼珠沙華公園',
        },
        {
          id: 'kinchakuda-name',
          x: 0.55,
          y: 0.35,
          title: 'きんちゃくの形',
          body: '高麗川の蛇行によって形づくられた土地で、その形がきんちゃくの形に似ていることから巾着田と呼ばれるようになった。',
          source:
            'https://www.city.hidaka.lg.jp/soshiki/shiminseikatsu/sangyoshinko/shokokanko/kanko/kankou/5810.html',
          mapQuery: '巾着田',
        },
        {
          id: 'kinchakuda-carpet',
          x: 0.8,
          y: 0.75,
          title: '赤いじゅうたん',
          body: '日高市は「辺り一面が真紅に染まり、まるで赤いじゅうたんを敷き詰めたようです」と記している。',
          source:
            'https://www.city.hidaka.lg.jp/soshiki/shiminseikatsu/sangyoshinko/shokokanko/kanko/kankou/5810.html',
          mapQuery: '日高市 巾着田',
        },
      ],
    },
    {
      id: 'soka-matsubara',
      name: '草加松原',
      area: '草加市',
      image: '/assets/scenery/saitama/scenes/06_soka_matsubara.webp',
      hotspots: [
        {
          id: 'soka-matsubara-meisho',
          x: 0.28,
          y: 0.35,
          title: 'おくのほそ道の風景地',
          body: '国指定名勝「おくのほそ道の風景地 草加松原」として、平成26年3月18日に指定された。',
          source:
            'https://www.city.soka.saitama.jp/cont/s2105/030/010/010/PAGE000000000000037968.html',
          mapQuery: '草加松原',
        },
        {
          id: 'soka-matsubara-count',
          x: 0.55,
          y: 0.62,
          title: '松は634本',
          body: '松の本数は、昭和20年ごろに600本以上、現在は634本である。',
          source:
            'https://www.city.soka.saitama.jp/cont/s2105/030/010/010/PAGE000000000000037968.html',
          mapQuery: '草加松原遊歩道',
        },
        {
          id: 'soka-matsubara-basho',
          x: 0.8,
          y: 0.8,
          title: '旅の初日に着いた宿',
          body: '元禄2年3月27日、松尾芭蕉は門人の河合曽良を伴って江戸を出発し、旅の初日に千住宿から出発したのち「草加という宿にたどり着きにけり」と記している。現在の暦では5月16日にあたる。',
          source:
            'https://www.city.soka.saitama.jp/cont/s2105/030/010/010/PAGE000000000000037968.html',
          mapQuery: '草加市 綾瀬川',
        },
      ],
    },
    {
      id: 'nakatsukyo',
      name: '中津峡',
      area: '秩父市',
      image: '/assets/scenery/saitama/scenes/07_nakatsukyo.webp',
      hotspots: [
        {
          id: 'nakatsukyo-meisho',
          x: 0.28,
          y: 0.3,
          title: '昭和18年に県指定名勝',
          body: '昭和18年3月31日に埼玉県指定名勝となった。所有は秩父市。',
          source: 'https://www.city.chichibu.lg.jp/4439.html',
          mapQuery: '中津峡',
        },
        {
          id: 'nakatsukyo-length',
          x: 0.55,
          y: 0.6,
          title: 'およそ10キロの渓谷',
          body: 'およそ10キロメートルにわたる渓谷で、荒川の支流である中津川が流れる。旧塩沢地区から中津川地区のあいだにあたる。',
          source: 'https://www.city.chichibu.lg.jp/4439.html',
          mapQuery: '秩父市 中津川',
        },
        {
          id: 'nakatsukyo-seasons',
          x: 0.8,
          y: 0.8,
          title: '春はつつじ、夏は青葉',
          body: '春はつつじ、夏は青葉、秋は紅葉と四季の変化に富み、清流と深い谷、奇岩怪石が連なる。',
          source: 'https://www.city.chichibu.lg.jp/4439.html',
          mapQuery: '中津峡 紅葉',
        },
      ],
    },
    {
      id: 'toki-no-kane',
      name: '時の鐘',
      area: '川越市',
      image: '/assets/scenery/saitama/scenes/08_toki_no_kane.webp',
      hotspots: [
        {
          id: 'toki-no-kane-bunkazai',
          x: 0.28,
          y: 0.35,
          title: '昭和33年に市指定文化財',
          body: '市指定有形文化財（建造物）で、昭和33年3月6日の指定。所有は川越市。',
          source:
            'https://www.city.kawagoe.saitama.jp/kurashi/bunka/1003787/1003798/1003884/1003925/1004039.html',
          mapQuery: '時の鐘 川越',
        },
        {
          id: 'toki-no-kane-size',
          x: 0.55,
          y: 0.3,
          title: '3重4階、総高さ約17.5メートル',
          body: '3重4階建で総高さは約17.5メートル。3重の屋根は銅板一文葺の方形造、初重と2重の庇は銅板瓦棒葺。',
          source:
            'https://www.city.kawagoe.saitama.jp/kurashi/bunka/1003787/1003798/1003884/1003925/1004039.html',
          mapQuery: '川越市幸町',
        },
        {
          id: 'toki-no-kane-sound',
          x: 0.8,
          y: 0.65,
          title: 'いまも1日に4回鳴る',
          body: '江戸時代初期の寛永年間に創建され、明治26年の大火で焼失して翌年に再建された。いまは自動鐘打機によって午前6時、正午、午後3時、午後6時の1日4回鐘をつく。平成8年に「残したい日本の音風景100選」に選ばれた。',
          source: 'https://www.city.kawagoe.saitama.jp/kanko/k-spots/1011557/1011562.html',
          mapQuery: '川越 時の鐘',
        },
      ],
    },
    {
      id: 'misotsuchi',
      name: '三十槌の氷柱',
      area: '秩父市',
      image: '/assets/scenery/saitama/scenes/09_misotsuchi.webp',
      hotspots: [
        {
          id: 'misotsuchi-natural',
          x: 0.28,
          y: 0.35,
          title: '岩清水が凍ってできる',
          body: '岩肌にしみ出る湧水が凍ってつくられる氷の芸術で、所在は秩父市大滝。',
          source: 'https://navi.city.chichibu.lg.jp/p_flower/1403/',
          mapQuery: '三十槌の氷柱',
        },
        {
          id: 'misotsuchi-size',
          x: 0.55,
          y: 0.6,
          title: '高さ約8メートル、幅約30メートル',
          body: '天然の氷柱は高さ約8メートル、幅約30メートルにもなる大規模な氷のオブジェ。天然の氷柱なので毎年、姿も変わる。',
          source: 'https://www.chichibuji.gr.jp/event/turara/',
          mapQuery: '秩父市大滝 三十槌',
        },
        {
          id: 'misotsuchi-season',
          x: 0.8,
          y: 0.78,
          title: '1月から2月の真冬に',
          body: '毎年1月から2月の真冬に現れる。すぐ近くには人工の氷柱もあり、そちらは幅約55メートル、高さ約25メートルと規模が違う。',
          source: 'https://saitama-riversupporters.pref.saitama.lg.jp/magazine/feature-misotsuchi/',
          mapQuery: '三十槌の氷柱 展望台',
        },
      ],
    },
    {
      id: 'yoshimi-hyakketsu',
      name: '吉見百穴',
      area: '吉見町',
      image: '/assets/scenery/saitama/scenes/10_yoshimi.webp',
      solemn: true,
      hotspots: [
        {
          id: 'yoshimi-hyakketsu-shiseki',
          x: 0.28,
          y: 0.35,
          title: '大正12年に国の史跡',
          body: '大正12年に国の史跡に指定された。現在確認できる横穴の数は219基である。',
          source: 'https://www.town.yoshimi.saitama.jp/soshiki/shogaigakushuk/7/909.html',
          mapQuery: '吉見百穴',
        },
        {
          id: 'yoshimi-hyakketsu-tombs',
          x: 0.55,
          y: 0.6,
          title: '古墳時代末の横穴墓群',
          body: '古墳時代の末期、6世紀末から7世紀後半に造られた横穴墓群である。また、ここに生育するヒカリゴケは国の天然記念物で、関東平野に生育していることは植物学上きわめて貴重とされる。',
          source: 'https://www.town.yoshimi.saitama.jp/soshiki/shogaigakushuk/7/909.html',
          mapQuery: '吉見町 吉見百穴',
        },
        {
          id: 'yoshimi-hyakketsu-wartime',
          x: 0.8,
          y: 0.78,
          title: '地下軍需工場が造られた',
          body: '昭和19年から20年に、吉見百穴とその周辺の丘陵地帯に大規模な地下軍需工場が造られた。工事は「吉松工事」と呼ばれ、3,000人から3,500人の朝鮮人労働者が携わった。終戦前に機械が搬入されエンジンの部品が製造され始めたが、本格的な生産の前に終戦を迎えた。',
          source: 'https://www.town.yoshimi.saitama.jp/soshiki/shogaigakushuk/7/909.html',
          mapQuery: '吉見百穴 地下軍需工場跡',
        },
      ],
    },
  ],
};
